import { DATE_FORMATS, DEFAULT_DATE_FORMAT, type DateFormat } from "./constants";

/** A user's chosen date format and IANA time zone. */
export interface DisplayPreferences {
  dateFormat: DateFormat;
  timeZone: string;
}

/** Key under the Supabase auth `user_metadata` jsonb where the preferences live. */
export const DISPLAY_PREFERENCES_METADATA_KEY = "display_preferences";

/** Stored shape inside `user_metadata`, snake_case like the rest of the persisted data. */
interface StoredDisplayPreferences {
  date_format: DateFormat;
  time_zone: string;
}

const isDateFormat = (value: unknown): value is DateFormat => DATE_FORMATS.includes(value as DateFormat);

const isTimeZone = (value: unknown): value is string => {
  if (typeof value !== "string" || !value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/**
 * Reads the saved preferences out of Supabase auth `user_metadata`, keeping only valid fields.
 *
 * @param metadata - The auth user's `user_metadata` (untrusted, user-writable jsonb).
 * @returns The fields the user has explicitly saved; missing or invalid fields are omitted.
 */
export function parseDisplayPreferences(metadata: unknown): Partial<DisplayPreferences> {
  const stored = (metadata as Record<string, unknown> | null | undefined)?.[DISPLAY_PREFERENCES_METADATA_KEY];
  if (!stored || typeof stored !== "object") return {};
  const { date_format, time_zone } = stored as Partial<StoredDisplayPreferences>;
  return {
    ...(isDateFormat(date_format) && { dateFormat: date_format }),
    ...(isTimeZone(time_zone) && { timeZone: time_zone }),
  };
}

/**
 * Builds the `user_metadata` patch for `supabase.auth.updateUser({ data })`.
 *
 * @param preferences - The preferences to save.
 * @returns A metadata patch keyed by {@link DISPLAY_PREFERENCES_METADATA_KEY}.
 */
export function toDisplayPreferencesMetadata(
  preferences: DisplayPreferences
): Record<string, StoredDisplayPreferences> {
  return {
    [DISPLAY_PREFERENCES_METADATA_KEY]: { date_format: preferences.dateFormat, time_zone: preferences.timeZone },
  };
}

/**
 * Suggests preferences from the browser's locale and time zone, for a user who hasn't chosen yet.
 * Pre-fills the profile picker, and through {@link resolveRequestDateFormat} sets the display format until the user saves.
 *
 * @param locale - BCP 47 locale to infer the date order from. Defaults to the browser's.
 * @returns A date format matching the locale's day/month/year order, and the browser's time zone.
 */
export function suggestDisplayPreferences(locale?: string): DisplayPreferences {
  const formatter = new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
  const order = formatter
    .formatToParts(new Date(2000, 11, 31))
    .map((part) => part.type)
    .filter((type) => type === "year" || type === "month" || type === "day");
  const dateFormat: DateFormat =
    order[0] === "year" ? "YYYY-MM-DD" : order[0] === "day" ? "DD/MM/YYYY" : DEFAULT_DATE_FORMAT;
  return { dateFormat, timeZone: formatter.resolvedOptions().timeZone };
}

/**
 * The date format a request should render with before any client code runs: the saved one, else
 * the one the request's `Accept-Language` suggests, so an unsaved en-GB user gets DD/MM on first paint.
 *
 * @param metadata - The auth user's `user_metadata`, or undefined when signed out.
 * @param acceptLanguage - The request's `Accept-Language` header, if any.
 * @returns The saved format, the locale's suggested format, or {@link DEFAULT_DATE_FORMAT}.
 */
export function resolveRequestDateFormat(metadata: unknown, acceptLanguage: string | null): DateFormat {
  const saved = parseDisplayPreferences(metadata).dateFormat;
  if (saved) return saved;
  const locale = acceptLanguage?.split(",")[0]?.split(";")[0]?.trim();
  if (!locale || locale === "*") return DEFAULT_DATE_FORMAT;
  try {
    return suggestDisplayPreferences(locale).dateFormat;
  } catch {
    return DEFAULT_DATE_FORMAT;
  }
}
